import { uniqBy } from 'lodash';
import type { GetTimesheetProposalApiResponse } from '../../../../redux/modules/timely/api-slice/GENERATED_ARTIFACT';
import type { ProposalLogEntry, RailsProposal } from '../types/types.TimesheetProposals';
import { partitionItemsByContainment } from './util.itemContainment';

const entryRangeMinutes = (
  entry: NonNullable<GetTimesheetProposalApiResponse['proposal']['entries']>[0]
): number => {
  if (!entry.started_at || !entry.ended_at) return 0;
  return Math.max(0, (Date.parse(entry.ended_at) - Date.parse(entry.started_at)) / (1000 * 60));
};

const getAssignedEntries = (entries: GetTimesheetProposalApiResponse['proposal']['entries']) => {
  return (entries ?? []).filter(entry => entry.timely_project_id !== null);
};

export const getAnalyticsForTimesheetSubmitted = (proposal: RailsProposal) => {
  // The Rails artifact types `log` as opaque `object[]`; narrow to the
  // structured shape (runtime payload is identical) so `.action` / `.details`
  // reads typecheck. See ProposalLogEntry.
  const logs = (proposal.log ?? []) as ProposalLogEntry[];
  const createdLog = logs.find(log => log.action === 'created');
  const details = createdLog?.details;

  const allItems = proposal.items ?? [];
  const realEntries = proposal.entries ?? [];
  const { byEntryId: itemsByEntryId, unassigned } = partitionItemsByContainment(
    allItems,
    realEntries
  );

  const assignedEntries = getAssignedEntries(proposal.entries);
  // An entry's authored range is the source of truth for its duration — items
  // contained within don't drive it.
  const finalDurationAssigned = assignedEntries.reduce(
    (sum, entry) => sum + entryRangeMinutes(entry),
    0
  );

  const logFilters = {
    entryReassigned: (log: any) =>
      log.action === 'entry_updated' && log.details?.updated_fields?.includes('timely_project_id'),
    entryNoteUpdated: (log: any) =>
      log.action === 'entry_updated' && log.details?.updated_fields?.includes('note'),
    itemAssigned: (log: any) => log.action === 'item_assigned',
    itemUnassigned: (log: any) => log.action === 'item_unassigned',
    itemReassigned: (log: any) =>
      log.action === 'item_updated' &&
      log.details?.updated_fields?.includes('timesheet_proposal_entry_id'),
    entryAdded: (log: any) => log.action === 'entry_added',
    entryRemoved: (log: any) => log.action === 'entry_removed',
  };

  const entryReassignedLogs = logs.filter(logFilters.entryReassigned);
  const entryNoteUpdatedLogs = logs.filter(logFilters.entryNoteUpdated);
  const itemAssignedLogs = logs.filter(logFilters.itemAssigned);
  const itemUnassignedLogs = logs.filter(logFilters.itemUnassigned);
  const itemReassignedLogs = logs.filter(logFilters.itemReassigned);
  const entryAddedLogs = logs.filter(logFilters.entryAdded);
  const entryRemovedLogs = logs.filter(logFilters.entryRemoved);

  // We avoid duplicates for now
  const entryReassignments = uniqBy(entryReassignedLogs, log => log.details?.entry_id);
  const entryNoteUpdates = uniqBy(entryNoteUpdatedLogs, log => log.details?.entry_id);
  // Entry splitting moves items between unassigned entries, creating spurious
  // item_assigned logs. Only count items moved to an actually-assigned entry.
  // entry.id is numeric on Rails (UUID on legacy TIC) while the log's
  // moved_to_entry_id arrives stringified — key both via String() so the
  // cross-id-space lookup reconciles regardless of source.
  const entryProjectMap = new Map(
    (proposal.entries ?? []).map(entry => [String(entry.id), entry.timely_project_id])
  );
  const itemAssignments = uniqBy(itemAssignedLogs, log => log.details?.item_id).filter(log => {
    const targetEntryId = log.details?.moved_to_entry_id;
    if (!targetEntryId) return true;
    return entryProjectMap.get(String(targetEntryId)) != null;
  });
  const itemUnassignments = uniqBy(itemUnassignedLogs, log => log.details?.item_id);
  const itemReassignments = uniqBy(itemReassignedLogs, log => log.details?.item_id);

  const originalDuration = details?.duration_minutes_assigned ?? 0;
  const originalTotalActivities =
    (details?.num_items_assigned ?? 0) + (details?.num_items_unassigned ?? 0);
  // Items corrected via entry-level reassignment (project changed on the entry itself).
  // Uses current entry items — if items were later moved out they'll be captured by
  // itemReassignments instead; the Set deduplicates across both paths.
  // entryReassignments come from logs (entry_id stringified); entry.id is numeric
  // on Rails. Compare in the string key space so the membership test reconciles.
  const reassignedEntryIds = new Set(entryReassignments.map(log => String(log.details?.entry_id)));
  const itemsFromReassignedEntries = (proposal.entries ?? [])
    .filter(entry => reassignedEntryIds.has(String(entry.id)))
    .flatMap(entry => (itemsByEntryId.get(entry.id) ?? []).map(item => item.id));

  const itemsCorrected = new Set([
    ...itemReassignments.map(log => log.details?.item_id),
    ...itemAssignments.map(log => log.details?.item_id),
    ...itemsFromReassignedEntries,
  ]).size;
  const finalProjectIds = assignedEntries.map(entry => entry.timely_project_id).filter(Boolean);

  const metadata = (proposal.metadata || {}) as Record<string, unknown>;

  const analytics = {
    original_total_entries: details?.num_entries_assigned,
    original_total_duration_minutes: details?.duration_minutes_assigned,
    final_total_entries: assignedEntries.length,
    final_total_duration_minutes: finalDurationAssigned,
    duration_modification_minutes: finalDurationAssigned - originalDuration,
    timesheet_date: proposal.date,
    entries_added: entryAddedLogs.length,
    entries_reassigned: entryReassignments.length,
    entries_removed: entryRemovedLogs.length,
    items_reassigned: itemReassignments.length,
    unassigned_activities_logged: itemAssignments.length,
    assigned_activities_removed: itemUnassignments.length,
    original_project_ids: details?.timely_project_ids,
    final_project_ids: finalProjectIds,
    notes_edited: entryNoteUpdates.length,
    original_total_duration_minutes_unassigned: details?.duration_minutes_unassigned,
    original_total_activities: originalTotalActivities || undefined,
    original_total_unassigned_activities: details?.num_items_unassigned,
    total_unassigned_activities: unassigned.length,
    accuracy_score:
      originalTotalActivities > 0
        ? Math.max(0, Math.round((1 - itemsCorrected / originalTotalActivities) * 10000) / 10000)
        : undefined,
    generation_mode: metadata.generation_mode,
    model_provider: metadata.provider,
    effort_level: metadata.effort,
    chosen_interval_minutes: metadata.interval_minutes,
    note_language: metadata.note_language,
    total_input_tokens: metadata.total_input_tokens,
    total_output_tokens: metadata.total_output_tokens,
    entries_input_tokens: metadata.entries_input_tokens,
    entries_output_tokens: metadata.entries_output_tokens,
    summaries_input_tokens: metadata.summaries_input_tokens,
    summaries_output_tokens: metadata.summaries_output_tokens,
    entries_cost: metadata.entries_cost,
    summaries_cost: metadata.summaries_cost,
    total_cost: metadata.total_cost,
  };

  // Remove any null values
  return Object.fromEntries(
    Object.entries(analytics).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.map(v => v ?? undefined) : value,
    ])
  );
};
